
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface User {
  full_name: string;
  avatar_url?: string | null;
  guest_avatar_url?: string | null;
  user_id?: string;
  isHost?: boolean;
  invite_id?: string;
  rsvp_status?: 'accepted' | 'declined' | 'invited' | 'maybe';
}

export interface Invite {
  invite_id: string;
  display_name: string;
  phone_number?: string;
  rsvp_status?: 'invited' | 'accepted' | 'declined' | 'maybe';
  feedback_text?: string | null;
  suggested_venue?: string | null;
  selected_times?: string[] | null;
  selected_dates?: string[] | null;
  claimed_by_user_id?: string | null;
  user_phone_number?: string | null;
  avatar_url?: string | null;
  user_full_name?: string | null;
  guest_avatar_url?: string | null;
}

export const useEventGuests = (eventId: string) => {
  // Combined query to fetch both host and invites
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['event-guests-and-host', eventId],
    queryFn: async () => {
      // Use Promise.all to parallelize the queries
      const [eventResponse, invitesResponse] = await Promise.all([
        // Fetch event host details
        supabase
          .from('events')
          .select(`
            host_user_id,
            host:host_user_id(
              user_id,
              full_name,
              avatar_url
            )
          `)
          .eq('event_id', eventId)
          .single(),

        // Fetch all invites for the event
        supabase
          .from('event_invites')
          .select(`
            invite_id,
            display_name,
            phone_number,
            rsvp_status,
            feedback_text,
            suggested_venue,
            selected_times,
            selected_dates,
            claimed_by_user_id,
            user:claimed_by_user_id(
              phone_number,
              avatar_url,
              full_name
            ),
            guest_avatar_url
          `)
          .eq('event_id', eventId)
          .order('created_at', { ascending: true })
      ]);

      // Handle errors from either query
      if (eventResponse.error) {
        throw eventResponse.error;
      }

      if (invitesResponse.error) {
        throw invitesResponse.error;
      }

      // Extract data from responses
      const eventData = eventResponse.data;
      const invitesData = invitesResponse.data || [];

      // Process host data
      const hostUser = eventData?.host ? {
        full_name: eventData.host.full_name || 'Host',
        avatar_url: eventData.host.avatar_url,
        user_id: eventData.host.user_id,
        isHost: true
      } : null;

      // Process invites data - optimize by using map once
      const processedInvites = invitesData.map((invite: any): Invite => ({
        invite_id: invite.invite_id,
        display_name: invite.display_name || 'Guest',
        phone_number: invite.phone_number,
        rsvp_status: invite.rsvp_status || 'invited',
        feedback_text: invite.feedback_text,
        suggested_venue: invite.suggested_venue,
        selected_times: invite.selected_times,
        selected_dates: invite.selected_dates,
        claimed_by_user_id: invite.claimed_by_user_id,
        user_phone_number: invite.user?.phone_number,
        avatar_url: invite.user?.avatar_url,
        user_full_name: invite.user?.full_name,
        guest_avatar_url: invite.guest_avatar_url
      }));

      // Create guest lists more efficiently
      const acceptedGuests = [];
      const invitedGuests = [];
      
      // Process invites in a single loop instead of multiple array transformations
      for (const invite of processedInvites) {
        // Create the base guest object for both arrays
        const guestObject = {
          full_name: invite.display_name,
          avatar_url: invite.avatar_url,
          guest_avatar_url: invite.guest_avatar_url,
          invite_id: invite.invite_id,
          rsvp_status: invite.rsvp_status
        };
        
        // Add to appropriate arrays
        invitedGuests.push(guestObject);
        
        if (invite.rsvp_status === 'accepted') {
          acceptedGuests.push({...guestObject, rsvp_status: 'accepted'});
        }
      }

      return {
        hostUser,
        invites: processedInvites,
        acceptedGuests,
        invitedGuests
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minute cache
    enabled: !!eventId,
  });

  return {
    invites: data?.invites || [],
    hostUser: data?.hostUser || null,
    acceptedGuests: data?.acceptedGuests || [],
    invitedGuests: data?.invitedGuests || [],
    isLoading,
    isLoadingInvites: isLoading, // Alias to match what components expect
    refetch
  };
};
